/**
 * The drill — the chain an operator opens under a figure on the analytics
 * report, and what each link in it means.
 *
 * One module-scope cell holds the chain: the nodes opened so far, deepest
 * last. A press feeds `drillOpen$`, a crumb or a dismiss feeds `drillPop$`,
 * and `report.view.ts` owns the single subscription that turns those into
 * overlays — nothing here touches the DOM.
 *
 * EVERY ANSWER IS SUMMED OUT OF THE ANSWER ON SCREEN. `reportState`'s own
 * rows are the only source, read at resolve time, so a decomposition cannot
 * disagree with the figure it opened out of and turning a knob moves every
 * open panel with it. There is one set of rows and no second derivation.
 *
 * PROSE LEAVES AS CATALOG KEYS, NEVER AS SENTENCES. A `Phrase` is a key the
 * body resolves against the operator's own locale, an already-formatted datum
 * that no catalog carries (a campaign source), or a bare instant — the same
 * split `report.state.ts` makes when it hands the surface `labelKey`s rather
 * than words.
 *
 * AND A DATE LEAVES UNPRINTED. Whether a time reads 21:04 or 09:04 PM, and
 * whether a day reads `Tue, Aug 25` or `Di., 25. Aug.`, is a fact about the
 * READER, not about the datum: `Aug` and `Di.` are words in a language, where
 * a grouping separator is a convention about a number. A module-scope cell has
 * no host to ask who is reading — the theme's locale reaches a component and
 * only a component — so every instant and every day crosses to the surface as
 * the date it is and `phraseText` prints it there, beside the words it sits
 * in.
 */

import { formatNumber } from "@mhmo91/rx";
import { computed, state } from "@mhmo91/schmancy/state";
import type { TextKey } from "@momo/i18n";
import { type AnalyticsReport, Ticket } from "@momo/types";
import { Subject } from "rxjs";
import { session } from "../../auth/session.state.ts";
import { canScanDoor } from "../../checkin/checkin.state.ts";
import {
	doorLabel,
	doorLabels,
} from "../../checkin/door-labels/door-labels.state.ts";
import {
	consoleFiguresByType,
	consoleOrders,
	consoleOrdersStatus,
	consoleRowsAnswered,
	consoleTickets,
	consoleTicketsStatus,
	type OrderRow,
} from "../../events/console/console.state.ts";
import type { DrillNode, FigureUnit } from "../analytics-figure.view.ts";
import { formatFigure } from "../analytics-figure.view.ts";
import {
	calendarDay,
	DIRECT_CHANNEL,
	FIGURE_LOCALE,
	reportKnobs,
	reportState,
} from "../report.state.ts";

// The node a press carries is `<analytics-figure>`'s own contract; what
// resolves it is this module's.
export type { DrillNode };

export type DrillKind = DrillNode["kind"];

/** One line of rendered text: a catalog key the body translates, a datum
 *  already in its printed form because no catalog carries a campaign called
 *  `instagram`, or an instant left unprinted because how a clock reads is a
 *  fact about the reader and this module cannot see one. */
export type Phrase =
	| {
			readonly key: TextKey;
			/** A param may itself be a phrase — a funnel stage names itself with
			 *  the very key the panel's own funnel row prints, so the row and the
			 *  panel it opens can never call one stage two things. */
			readonly params?: Readonly<Record<string, string | number | Phrase>>;
	  }
	| { readonly text: string }
	/** Several phrases on one line, joined by this surface's own separator.
	 *  Each element resolves against the same reader as the line it sits in, so
	 *  a day inside a scope reads in the reader's own words like the sentence
	 *  around it — which a pre-joined string could not, having been printed
	 *  before any reader was known. */
	| { readonly list: readonly Phrase[] }
	/** A calendar day, weekday first — `Tue, Aug 25` against `Di., 25. Aug.`,
	 *  which is what an operator scanning a column of days tells apart at a
	 *  glance. A weekday and a month are words in a language, so a day crosses
	 *  unprinted like the clock below it. */
	| { readonly day: string }
	/** A span of calendar days — `24 – 28 Aug` against `24.–28. Aug.`. Which
	 *  parts a locale names once across a span, and what it puts between them,
	 *  is the formatter's answer to give; a hand-joined dash would fix one
	 *  language's for every reader. */
	| { readonly from: string; readonly to: string }
	/** A time of day — 21:04 against 09:04 PM. */
	| { readonly clock: string }
	/** The same instant carrying the day it fell on, for a line a recalled
	 *  answer can put weeks behind today. `clock` wherever the day is already
	 *  named beside it, `stamp` wherever it is not. */
	| { readonly stamp: string };

/** Who a phrase is rendered FOR: their catalog and their formatting locale,
 *  arriving as ONE value rather than as two arguments. They are two facets of
 *  one reader, and passed separately a call site is free to hand over one
 *  reader's words beside another's clock — which is the defect the instants
 *  above exist to make unrepresentable. A `LocalizeMixin` host satisfies this
 *  as it stands, so a body passes itself. */
export interface PhraseReader {
	t(key: TextKey, params?: Record<string, string | number>): string;
	readonly locale: string;
}

/** A phrase rendered for one reader. This module holds neither engine nor
 *  locale — both arrive with the reader — and a param that is itself a phrase
 *  is resolved first, so a funnel stage in a title reads with the very word
 *  the funnel row beside it prints, and an instant inside a sentence reads on
 *  the same clock as the sentence around it. */
export function phraseText(phrase: Phrase, reader: PhraseReader): string {
	if ("text" in phrase) return phrase.text;
	if ("list" in phrase)
		return phrase.list.map((entry) => phraseText(entry, reader)).join(" · ");
	if ("day" in phrase)
		return new Date(`${phrase.day}T00:00:00.000Z`).toLocaleDateString(
			reader.locale,
			{ weekday: "short", day: "numeric", month: "short", timeZone: "UTC" },
		);
	if ("from" in phrase)
		return new Intl.DateTimeFormat(reader.locale, {
			day: "numeric",
			month: "short",
			timeZone: "UTC",
		}).formatRange(
			new Date(`${phrase.from}T00:00:00.000Z`),
			new Date(`${phrase.to}T00:00:00.000Z`),
		);
	if ("clock" in phrase)
		return new Date(phrase.clock).toLocaleTimeString(reader.locale, {
			hour: "2-digit",
			minute: "2-digit",
		});
	if ("stamp" in phrase)
		return new Date(phrase.stamp).toLocaleString(reader.locale, {
			dateStyle: "medium",
			timeStyle: "short",
		});
	return reader.t(
		phrase.key,
		Object.fromEntries(
			Object.entries(phrase.params ?? {}).map(([name, value]) => [
				name,
				typeof value === "object" ? phraseText(value, reader) : value,
			]),
		),
	);
}

export const drillChain = state<DrillNode[]>(
	"admin/analytics-drill-chain",
).memory([]);

/** A press on a figure, and the level of the chain it was pressed from — the
 *  report surface is 0, a panel is its own depth. The chain is a stack, so
 *  opening from level N means the chain becomes its own first N nodes plus
 *  this one; a press that did not say where it came from would land under
 *  levels it replaces. Fed synchronously from the handler so the overlay
 *  service still has the gesture to anchor against. */
export const drillOpen$ = new Subject<{
	readonly node: DrillNode;
	readonly from: number;
}>();

/** Close every level deeper than this one. `0` closes the chain. */
export const drillPop$ = new Subject<number>();

export function pushDrill(node: DrillNode): number {
	drillChain.push(node);
	return drillChain.value.length;
}

/** Shrink only, so a dismissal landing after the chain has already been cut
 *  below its own level does nothing. That is what makes an overlay's teardown
 *  safe to run late — but only while the chain has not GROWN past that level
 *  since, which is why a press pops to its own level before it pushes. */
export function truncateDrill(depth: number): void {
	if (drillChain.value.length > depth)
		drillChain.replace(drillChain.value.slice(0, depth));
}

/** One part of a decomposition. `open` is what the part's own figure opens —
 *  every part is itself a figure, which is what turns a drill into a chain.
 *  Null where the answer beneath it is one this surface cannot yet produce,
 *  which prints the part plain rather than offering a gesture that opens
 *  nothing. */
export interface DrillPart {
	readonly id: string;
	readonly label: Phrase;
	readonly value: number;
	readonly open: DrillNode | null;
}

export type DrillAnswer =
	| {
			readonly body: "parts";
			readonly title: Phrase;
			readonly whole: number;
			readonly unit: FigureUnit;
			readonly parts: readonly DrillPart[];
			/** The sentence the sum bar underneath is proving. */
			readonly claim: Phrase;
	  }
	| {
			readonly body: "provenance";
			readonly title: Phrase;
			readonly whole: number;
			readonly unit: FigureUnit;
			/** What the answer was asked for — the knobs in force when it was
			 *  derived, and when that was. */
			readonly derivation: readonly {
				readonly label: Phrase;
				readonly value: Phrase;
			}[];
			/** The collections the answer was read out of, and what each one
			 *  contributed. Naming them is what makes the number checkable
			 *  against the database rather than trusted. */
			readonly sources: readonly {
				readonly path: string;
				readonly read: Phrase;
				/** How many documents of this kind the call read. The count carries
				 *  the scale; the sentence beside it carries what they were for. */
				readonly documents: number;
			}[];
			/** How much of the answer was recalled rather than derived, and which
			 *  days were not. A row per day is thirty identical marks in which the
			 *  one day that differs disappears — the count carries the rule and
			 *  the named days carry the exceptions. */
			readonly freshness: {
				readonly stored: number;
				readonly total: number;
				readonly recomputed: readonly Phrase[];
			};
			/** What the visits figure becomes at each rung of the gap ladder, the
			 *  one in force marked. Empty on a figure the gap does not move. */
			readonly sensitivity: readonly {
				readonly minutes: number;
				readonly visits: number;
				readonly current: boolean;
			}[];
			/** Why this figure legitimately disagrees with the one printed beside
			 *  it. A surface that prints two numbers of the same thing owes the
			 *  reader the reason, or one of them looks like a bug. */
			readonly neighbour: {
				readonly claim: Phrase;
				readonly because: Phrase;
			} | null;
	  }
	| {
			readonly body: "records";
			readonly title: Phrase;
			readonly lead: Phrase;
			/** What the list is a list OF, and how much of it is on screen. A page
			 *  that stated no bound would read as the whole set. */
			readonly bound: Phrase;
			readonly rows: readonly {
				readonly id: string;
				readonly headline: string;
				readonly detail: Phrase;
				/** The row's own figure in cents — the handle, where the row has
				 *  somewhere to go. Absent on a record whose fact is not a quantity:
				 *  an admission is a time and a door. */
				readonly amountCents: number | null;
				readonly note: Phrase | null;
				readonly status: Phrase;
				readonly open: DrillNode | null;
			}[];
	  }
	| {
			readonly body: "boundary";
			readonly title: Phrase;
			/** Which edge this is. Not an error in any of its forms — the system
			 *  working exactly as designed — so none of them wears an error tone. */
			readonly reason: "privacy" | "bounded";
			readonly lead: Phrase;
			readonly refusal: Phrase;
			/** What the surface WILL show. A refusal that leaves the operator at a
			 *  dead end reads as the product hiding something. */
			readonly instead: readonly {
				readonly label: Phrase;
				readonly open: DrillNode;
			}[];
	  };

/** The decomposition half of the union, for a body and a test that only ever
 *  handle that half. */
export type PartsAnswer = Extract<DrillAnswer, { body: "parts" }>;

/** The account half of the union. */
export type ProvenanceAnswer = Extract<DrillAnswer, { body: "provenance" }>;

/** The business-record half of the union. */
export type RecordsAnswer = Extract<DrillAnswer, { body: "records" }>;

const EMPTY_ANSWER: AnalyticsReport.AnalyticsReportOutput = {
	days: [],
	revenueCentsByDay: {},
	campaignSources: [],
	campaigns: [],
	computedAt: new Date(0).toISOString(),
	servedFromCache: false,
	evidence: {
		pageViewEvents: 0,
		basketChangeEvents: 0,
		orderActionEvents: 0,
		checkoutStageEvents: 0,
		buyerRecords: 0,
		ordersRead: 0,
		cachedRowsRead: 0,
	},
};

function answer(): AnalyticsReport.AnalyticsReportOutput {
	return reportState.value.answer ?? EMPTY_ANSWER;
}

/** The stage labels the panel itself prints, reused verbatim so a funnel row
 *  and the panel it opens name the same stage with the same word. */
const STAGE_LABEL = {
	eventPageLands: "admin.analytics.stageEventPage",
	contactCompleted: "admin.analytics.stageContact",
	authorising: "admin.analytics.stageAuthorising",
	paid: "admin.analytics.stagePaid",
} as const satisfies Readonly<
	Record<
		"eventPageLands" | "contactCompleted" | "authorising" | "paid",
		TextKey
	>
>;

const OUTCOME_LABEL = {
	soldOut: "admin.analytics.outcomeSoldOut",
	notEligible: "admin.analytics.outcomeNotEligible",
	internal: "admin.analytics.outcomeInternal",
	licenceReadOnly: "admin.analytics.outcomeLicenceReadOnly",
	abandoned: "admin.analytics.outcomeAbandoned",
	expired: "admin.analytics.outcomeExpired",
} as const satisfies Readonly<Record<string, TextKey>>;

/** What a figure IS, for the crumb that names the step into it. A crumb names
 *  the subject, not the panel — "Visits › Thu, 20 Aug" reads as a route, where
 *  the panel's own full title repeated at every depth would not. */
const SUBJECT_LABEL = {
	visits: "admin.analytics.kpiVisits",
	visitors: "admin.analytics.kpiVisitors",
	returning: "admin.analytics.kpiReturning",
	revenue: "admin.analytics.kpiRevenue",
	channel: "admin.analytics.colChannel",
	"drop.payment": "admin.analytics.drill.titleDropPayment",
	"funnel.eventPageLands": "admin.analytics.stageEventPage",
	"funnel.contactCompleted": "admin.analytics.stageContact",
	"funnel.authorising": "admin.analytics.stageAuthorising",
	"funnel.paid": "admin.analytics.stagePaid",
	"visitor.timeline": "admin.analytics.drill.wallPrivacyTitle",
	"campaign.bounded": "admin.analytics.drill.wallBoundedTitle",
	conversion: "admin.analytics.kpiConversion",
	dwell: "admin.analytics.kpiDwell",
	retrieval: "admin.analytics.retrievalTitle",
	refusal: "admin.analytics.outcomesTitle",
} as const satisfies Readonly<Record<string, TextKey>>;

/** What a figure is called, for a title and a crumb alike. A subject with no
 *  catalog name prints its own key rather than a blank. */
function subjectPhrase(subject: string): Phrase {
	const named = SUBJECT_LABEL[subject as keyof typeof SUBJECT_LABEL];
	return named === undefined ? { text: subject } : { key: named };
}

/** The figure a node is about, in the unit it is about it in — the number the
 *  account below it is accounting for. */
function figureOf(node: DrillNode): { value: number; unit: FigureUnit } {
	const days = daysOf(node);
	switch (node.subject) {
		case "visits":
			return { value: sum(days.map((row) => row.visits)), unit: "count" };
		case "visitors":
			return { value: sum(days.map((row) => row.visitors)), unit: "count" };
		case "revenue":
			return { value: revenueOf(days), unit: "money" };
		case "conversion": {
			// Paid against VISITS, the same denominator the campaign rows use, so
			// one word means one thing on the whole surface. The other rate — of
			// the baskets started, how many paid — is the funnel's own last band.
			const visits = sum(days.map((row) => row.visits));
			const paid = sum(days.map((row) => row.funnel.paid));
			return {
				value: visits === 0 ? 0 : (paid / visits) * 100,
				unit: "percent",
			};
		}
		case "returning": {
			const returning = sum(days.map((row) => row.returningBuyers));
			const known = returning + sum(days.map((row) => row.newBuyers));
			return {
				value: known === 0 ? 0 : (returning / known) * 100,
				unit: "percent",
			};
		}
		case "dwell": {
			// The median OF the daily medians — not the median visit of the range,
			// which would need the raw distribution the stored row does not keep.
			// The neighbour section says so rather than letting the label imply a
			// figure the evidence cannot produce.
			const medians = days
				.map((row) => row.medianDwellSeconds)
				.toSorted((a, b) => a - b);
			return {
				value: medians[Math.floor(medians.length / 2)] ?? 0,
				unit: "seconds",
			};
		}
		case "retrieval": {
			const channel = scopeValue(node, "outcome");
			return {
				value: sum(
					days.map((row) =>
						channel === null
							? Object.values(row.orderActions).reduce((a, b) => a + b, 0)
							: (row.orderActions[channel as keyof typeof row.orderActions] ??
								0),
					),
				),
				unit: "count",
			};
		}
		case "refusal": {
			const outcome = scopeValue(node, "outcome");
			if (outcome === "abandoned")
				return {
					value: sum(days.map((row) => row.funnel.abandoned)),
					unit: "count",
				};
			if (outcome === "expired")
				return {
					value: sum(days.map((row) => row.funnel.expired)),
					unit: "count",
				};
			return {
				value: sum(
					days.map(
						(row) =>
							row.funnel.refused[outcome as keyof typeof row.funnel.refused] ??
							0,
					),
				),
				unit: "count",
			};
		}
		case "channel": {
			const source = scopeValue(node, "channel") ?? DIRECT_CHANNEL;
			return {
				value: sum(days.map((row) => row.utm[source]?.visits ?? 0)),
				unit: "count",
			};
		}
		case "funnel.eventPageLands":
		case "funnel.contactCompleted":
		case "funnel.authorising":
		case "funnel.paid": {
			const stage = node.subject.slice(
				"funnel.".length,
			) as keyof typeof STAGE_LABEL;
			return {
				value: sum(days.map((row) => row.funnel[stage])),
				unit: "count",
			};
		}
		case "drop.payment":
			return {
				value: sum(days.map((row) => row.funnel.authorising - row.funnel.paid)),
				unit: "count",
			};
		default:
			return { value: 0, unit: "count" };
	}
}

/** Above this many days a by-day decomposition is confetti — thirty segments
 *  none of which can be told from its neighbour — so the range decomposes into
 *  weeks first and each week into its own days. The chain gains a level that
 *  means something instead of a bar that does not. */
const DAYS_READ_DIRECTLY = 10;

/** The range split into calendar chunks of at most seven days, counted back
 *  from the most recent day so the last chunk is the one still running. */
function weeksOf(days: readonly AnalyticsReport.ReportedDay[]): readonly {
	readonly id: string;
	readonly days: readonly AnalyticsReport.ReportedDay[];
}[] {
	const chunks: AnalyticsReport.ReportedDay[][] = [];
	for (let end = days.length; end > 0; end -= 7)
		chunks.unshift(days.slice(Math.max(0, end - 7), end));
	return chunks
		.filter((chunk) => chunk.length > 0)
		.map((chunk) => ({ id: chunk[0]?.date ?? "", days: chunk }));
}

/** A week names the span it covers, which is what an operator reads it by. A
 *  week whose days the answer no longer holds has only its own first day left
 *  to name itself by. */
function weekLabel(firstDate: string): Phrase {
	const week = weeksOf(answer().days).find((w) => w.id === firstDate);
	const last = week?.days[week.days.length - 1]?.date;
	return last === undefined
		? { day: firstDate }
		: { from: firstDate, to: last };
}

function scopeValue(node: DrillNode, axis: string): string | null {
	const hit = node.scope.find((entry) => entry.startsWith(`${axis}:`));
	return hit ? hit.slice(axis.length + 1) : null;
}

/** The one-line rendering of a scope entry — what a crumb says, and what a
 *  title interpolates. */
export function scopeLabel(entry: string): Phrase {
	const [axis, ...rest] = entry.split(":");
	const value = rest.join(":");
	if (axis === "day") return { day: value };
	if (axis === "week") return weekLabel(value);
	if (axis === "channel") return channelLabel(value);
	return { text: value };
}

/** A campaign source is the operator's own word, printed as it arrived —
 *  except the absence of one, which the panel already names in its infographic. */
function channelLabel(source: string): Phrase {
	return source === DIRECT_CHANNEL
		? { key: "admin.analytics.channelDirect" }
		: { text: source };
}

/** The days a node's scope admits — the whole range, one week of it, or one
 *  day of it. */
function daysOf(node: DrillNode): readonly AnalyticsReport.ReportedDay[] {
	const days = answer().days;
	const day = scopeValue(node, "day");
	if (day) return days.filter((row) => row.date === day);
	const week = scopeValue(node, "week");
	if (week) return weeksOf(days).find((w) => w.id === week)?.days ?? days;
	return days;
}

/** The reserved key the stored day folds every campaign past the twentieth
 *  into. It is not a campaign, so it never decomposes as one. */
const BOUNDED_SOURCE = "other";

/** Where a decomposition would have to name a person to go further. Scoped to
 *  a day so its own door — the same figure split by campaign — has one to open
 *  onto; the surface's last day is the one the operator was last reading. */
function privacyEdge(scope: readonly string[]): DrillNode {
	const day = scope.find((entry) => entry.startsWith("day:"));
	const days = answer().days;
	return {
		kind: "boundary",
		subject: "visitor.timeline",
		scope: [day ?? `day:${days[days.length - 1]?.date ?? ""}`],
	};
}

/** The two edges, and the doors beside each. A door is offered only where it
 *  opens something — a refusal handing over a dead link would say the surface
 *  does not know its own line either. */
function wall(node: DrillNode): DrillAnswer | null {
	const day =
		node.scope.find((entry) => entry.startsWith("day:")) ??
		`day:${answer().days[answer().days.length - 1]?.date ?? ""}`;
	// One door per KIND of answer — a smaller split, the arithmetic — never two
	// of the same kind wearing the same glyph, which would say the offers are
	// interchangeable when the whole point is that they are not.
	const offers: readonly { label: Phrase; open: DrillNode }[] = [
		{
			label: {
				key:
					node.subject === "campaign.bounded"
						? "admin.analytics.drill.doorNamedCampaigns"
						: "admin.analytics.drill.doorSplitByCampaign",
			},
			open: { kind: "parts", subject: "visits", scope: [day] },
		},
		{
			label: { key: "admin.analytics.drill.doorHowDerived" },
			open: { kind: "provenance", subject: "visits", scope: [] },
		},
		{
			label: { key: "admin.analytics.drill.doorTheOrders" },
			open: { kind: "records", subject: "orders", scope: [day] },
		},
	];
	const doors = offers.filter((door) => answerable(door.open));
	if (doors.length === 0) return null;
	return node.subject === "campaign.bounded"
		? {
				body: "boundary",
				reason: "bounded",
				title: { key: "admin.analytics.drill.wallBoundedTitle" },
				lead: { key: "admin.analytics.drill.wallBoundedLead" },
				refusal: { key: "admin.analytics.drill.wallBoundedRefusal" },
				instead: doors,
			}
		: {
				body: "boundary",
				reason: "privacy",
				title: { key: "admin.analytics.drill.wallPrivacyTitle" },
				lead: { key: "admin.analytics.drill.wallPrivacyLead" },
				refusal: { key: "admin.analytics.drill.wallPrivacyRefusal" },
				instead: doors,
			};
}

/** How many records one page shows. A day of a busy show has more orders than
 *  a reader scans, and a list that stated no bound would read as the whole
 *  set — so the page is bounded and the bound is printed. */
const RECORD_PAGE = 12;

/**
 * The business records behind a figure — the orders themselves, and the
 * tickets one order issued.
 *
 * NO SECOND READ. The console this panel is mounted inside already holds every
 * order and ticket of its event, live, behind the very permission the report
 * rides (`tickets.reports.view`). Reading them again would be a second
 * representation of the same documents, free to disagree with the console's
 * own orders list about what an order says.
 *
 * NOTHING NEW IS EXPOSED. These are the operator's own business documents,
 * shown to an actor who can already open them one destination away. That is
 * exactly why the privacy wall stands where it does and not here: an aggregate
 * does not open into a visitor, and an order was never an aggregate.
 */
function records(node: DrillNode): DrillAnswer | null {
	// An empty orders cell means one of three things — this event has none,
	// this session was never admitted to them, or they have not all landed —
	// and only a `ready` read tells them apart. A figure that opened a
	// confidently empty list built from an absence would be stating a fact
	// nobody established.
	if (!consoleRowsAnswered(consoleOrdersStatus.value)) return null;
	const orders = consoleOrders.value;

	if (node.subject === "tickets") {
		const orderId = scopeValue(node, "order");
		const order = orders.find((row) => row.id === orderId);
		if (order === undefined || !consoleRowsAnswered(consoleTicketsStatus.value))
			return null;
		const tickets = consoleTickets.value.filter(
			(ticket) => ticket.orderId === order.id,
		);
		const typesById = consoleFiguresByType.get();
		return {
			body: "records",
			title: {
				key: "admin.analytics.drill.titleTickets",
				params: { invoice: order.invoiceNumber ?? order.id },
			},
			lead: {
				key: "admin.analytics.drill.recordsLeadTickets",
				params: {
					buyer: order.buyerName,
					total: formatFigure(grossCents(order), "money", FIGURE_LOCALE),
				},
			},
			bound:
				tickets.length === 1
					? { key: "admin.analytics.drill.recordsBoundTicketsOne" }
					: {
							key: "admin.analytics.drill.recordsBoundTickets",
							params: { count: tickets.length },
						},
			rows: tickets.map((ticket) => ({
				id: ticket.id,
				// The printed reference, never the doc id: that encodes the order
				// key, which a ticket handed to somebody else must not carry.
				headline: Ticket.ticketDisplayId(ticket.id),
				detail: {
					text: typesById?.get(ticket.typeId)?.name ?? ticket.typeId,
				},
				// An admission is a time and a door, never a quantity — so the row
				// carries no figure and no rule to press.
				amountCents: null,
				note:
					ticket.redeemedAt == null
						? { key: "admin.analytics.drill.recordsNotAdmitted" }
						: {
								key: "admin.analytics.drill.recordsAdmitted",
								params: {
									// The day is already on the panel's own title, so the
									// admission needs only the hour it happened at.
									time: {
										clock: new Date(ticket.redeemedAt.toMillis()).toISOString(),
									},
									door: doorPhrase(ticket.redeemedBy ?? null),
								},
							},
				status: {
					key:
						ticket.status === "redeemed"
							? "admin.analytics.drill.recordsStatusRedeemed"
							: "admin.analytics.drill.recordsStatusValid",
				},
				open: null,
			})),
		};
	}

	const date = scopeValue(node, "day");
	if (date === null) return null;
	const source = scopeValue(node, "channel");
	// The day is the operator's own calendar day — the same clock the range
	// they picked is expressed in, so the drill and the picker never disagree
	// about which day "Saturday" was.
	const onDay = orders.filter(
		(row) =>
			row.paidAt != null &&
			calendarDay(new Date(row.paidAt.toMillis())) === date,
	);
	const scoped =
		source === null
			? onDay
			: onDay.filter(
					(row) => (row.channel?.utm?.utm_source ?? null) === source,
				);
	const ranked = scoped.toSorted(
		(left, right) => grossCents(right) - grossCents(left),
	);
	const page = ranked.slice(0, RECORD_PAGE);
	return {
		body: "records",
		title:
			source === null
				? {
						key: "admin.analytics.drill.titleOrdersOfDay",
						params: { day: { day: date } },
					}
				: {
						key: "admin.analytics.drill.titleOrdersOfChannel",
						params: { channel: source, day: { day: date } },
					},
		lead: { key: "admin.analytics.drill.recordsLeadOrders" },
		bound: {
			key:
				ranked.length > page.length
					? "admin.analytics.drill.recordsBoundOrdersTop"
					: "admin.analytics.drill.recordsBoundOrders",
			params: { shown: page.length, total: ranked.length },
		},
		rows: page.map((order) => ({
			id: order.id,
			headline: order.buyerName,
			detail: ticketCountPhrase(order),
			amountCents: grossCents(order),
			note: null,
			status: {
				key:
					order.status === "refunded"
						? "admin.analytics.drill.recordsStatusRefunded"
						: "admin.analytics.drill.recordsStatusPaid",
			},
			open: {
				kind: "records",
				subject: "tickets",
				scope: [`order:${order.id}`],
			},
		})),
	};
}

/** Which door admitted a ticket. `redeemedBy` is the acting account's uid, not
 *  a door's name, so it resolves through the same label map the door log reads
 *  — and where no label exists the panel says so rather than printing a uid at
 *  a reader who has no way to recognise one. */
function doorPhrase(uid: string | null): Phrase {
	// Whether this session can scan is itself unanswered until its role has.
	if (session.value.status === "loading" || session.value.status === "binding")
		return { text: "" };
	// The labels are read only by a session that can scan, so for any other
	// they never answer and the door stays unknown to this reader.
	if (!canScanDoor(session.value))
		return { key: "admin.analytics.drill.recordsDoorUnknown" };
	// No door is named, known or unknown, before the labels have answered.
	if (doorLabels.value === null) return { text: "" };
	const label = uid === null ? null : doorLabel(uid);
	return label === null
		? { key: "admin.analytics.drill.recordsDoorUnknown" }
		: { text: label };
}

/** How many tickets an order issued, and which invoice — one key per count,
 *  because a catalog that carried only the plural would print "1 tickets" at
 *  every buyer who bought one. */
function ticketCountPhrase(order: OrderRow): Phrase {
	const tickets = order.lines.reduce(
		(total, line) => (line.kind === "ticket" ? total + line.qty : total),
		0,
	);
	const params = { tickets, invoice: order.invoiceNumber ?? order.id };
	return tickets === 1
		? { key: "admin.analytics.drill.recordsOrderDetailOne", params }
		: { key: "admin.analytics.drill.recordsOrderDetail", params };
}

/** What an order actually brought in — its lines less whatever went back. The
 *  same arithmetic the report's own revenue uses, so a day's records and the
 *  day's revenue figure are two views of one number. */
function grossCents(order: OrderRow): number {
	return (
		order.lines.reduce((total, line) => total + line.amount, 0) -
		(order.refundedAmount ?? 0)
	);
}

/**
 * The account of a figure: what it was asked for, what it was read out of, and
 * why it legitimately disagrees with the figure printed beside it. Every
 * section is derived from the same answer the surface is printing — an account
 * assembled from a second read could be an account of a different number.
 */
function account(node: DrillNode): DrillAnswer | null {
	const held = answer();
	const days = held.days;
	if (days.length === 0) return null;
	const figure = figureOf(node);
	const knobs = reportKnobs.value;
	const evidence = held.evidence;
	// Every arm of the account is a timeline read: the funnel's own stages are
	// `checkout_stage` facts on the same buyer timeline the visit arms come
	// from, so the path's document count sums all four.
	// `checkoutStageEvents` is absent while a server that predates it is still
	// answering — hosting and functions do not land together. Absent reads as
	// none reached rather than as a broken panel.
	const timelineDocuments =
		evidence.pageViewEvents +
		evidence.basketChangeEvents +
		evidence.orderActionEvents +
		(evidence.checkoutStageEvents ?? 0);
	const from = days[0]?.date ?? "";
	const to = days[days.length - 1]?.date ?? "";
	// What narrowed this figure, in the words each axis is already said in.
	// The campaign axis is named rather than printed: its value is a Meta id
	// this surface refuses to pass off as a campaign's name, and the cost card
	// the operator picked it on is what says which one.
	const narrowedBy: readonly Phrase[] = [
		...knobs.utmSources.map((source) => ({ text: source })),
		...node.scope.map(scopeLabel),
	];
	const scope: Phrase =
		knobs.utmCampaigns.length === 0
			? narrowedBy.length === 0
				? { key: "admin.analytics.drill.provScopeAll" }
				: { list: narrowedBy }
			: narrowedBy.length === 0
				? { key: "admin.analytics.campaignScope" }
				: {
						key: "admin.analytics.drill.provScopePlus",
						params: {
							others: { list: narrowedBy },
							campaign: { key: "admin.analytics.campaignScope" },
						},
					};
	return {
		body: "provenance",
		title: {
			key: "admin.analytics.drill.titleProvenance",
			params: { subject: subjectPhrase(node.subject) },
		},
		whole: figure.value,
		unit: figure.unit,
		derivation: [
			{
				label: { key: "admin.analytics.drill.provRange" },
				value:
					from === to
						? { day: from }
						: {
								key: "admin.analytics.drill.provRangeDays",
								params: {
									from: { day: from },
									to: { day: to },
									days: days.length,
								},
							},
			},
			{
				label: { key: "admin.analytics.drill.provGap" },
				value: {
					key: "admin.analytics.gapRule",
					params: { minutes: knobs.visitGapMinutes },
				},
			},
			{
				label: { key: "admin.analytics.drill.provScope" },
				value: scope,
			},
			{
				label: { key: "admin.analytics.drill.provComputedAt" },
				value: {
					key: held.servedFromCache
						? "admin.analytics.drill.provComputedCached"
						: "admin.analytics.drill.provComputedFresh",
					// The day and the hour both: a recalled answer's own reading can
					// be from a day the panel names nowhere, and a bare clock would
					// then read as this morning.
					params: { time: { stamp: held.computedAt } },
				},
			},
		],
		sources: [
			{
				path: "buyers/{buyerId}/timeline",
				documents: timelineDocuments,
				read:
					timelineDocuments === 0
						? { key: "admin.analytics.drill.provDocumentsNone" }
						: {
								key: "admin.analytics.drill.provDocumentsTimeline",
								params: {
									pageViews: formatNumber(
										evidence.pageViewEvents,
										FIGURE_LOCALE,
									),
									basketChanges: formatNumber(
										evidence.basketChangeEvents,
										FIGURE_LOCALE,
									),
									orderActions: formatNumber(
										evidence.orderActionEvents,
										FIGURE_LOCALE,
									),
									checkoutStages: formatNumber(
										evidence.checkoutStageEvents ?? 0,
										FIGURE_LOCALE,
									),
								},
							},
			},
			{
				path: "orders",
				documents: evidence.ordersRead,
				read: { key: "admin.analytics.drill.provSourceOrders" },
			},
			{
				path: "analyticsDaily/{date}",
				documents: evidence.cachedRowsRead,
				read: {
					key:
						evidence.cachedRowsRead > 0
							? "admin.analytics.drill.provSourceCacheUsed"
							: "admin.analytics.drill.provSourceCacheUnused",
				},
			},
		],
		freshness: {
			stored: days.filter((row) => row.servedFromCache).length,
			total: days.length,
			recomputed: days
				.filter((row) => !row.servedFromCache)
				.map((row) => ({ day: row.date })),
		},
		// The gap regroups the timeline into visits, so it moves the visit count
		// and everything derived from it — and moves nothing about an order, a
		// refusal or a retrieval, where a curve would be a decoration.
		sensitivity: GAP_MOVED_SUBJECTS.has(node.subject)
			? sensitivityOf(days, knobs.visitGapMinutes)
			: [],
		neighbour: neighbourOf(node.subject, days),
	};
}

/** The figures the visit gap actually moves. */
const GAP_MOVED_SUBJECTS: ReadonlySet<string> = new Set([
	"visits",
	"conversion",
	"channel",
	"dwell",
]);

/** The curve, summed across the range's own rows: each day already carries
 *  what it would have counted at every rung, so the range's curve is a sum
 *  rather than a second derivation that could disagree with it. */
function sensitivityOf(
	days: readonly AnalyticsReport.ReportedDay[],
	current: number,
): ProvenanceAnswer["sensitivity"] {
	const totals = new Map<number, number>();
	for (const row of days)
		for (const rung of row.visitsByGap)
			totals.set(rung.minutes, (totals.get(rung.minutes) ?? 0) + rung.visits);
	// The knob is a continuum and the ladder is five rungs, so a hand-set gap
	// lands between them. Its own figure is the one on screen, and a curve that
	// omitted exactly the setting in force would be answering a question nobody
	// asked.
	if (!totals.has(current))
		totals.set(
			current,
			days.reduce((total, row) => total + row.visits, 0),
		);
	return [...totals.entries()]
		.toSorted(([left], [right]) => left - right)
		.map(([minutes, visits]) => ({
			minutes,
			visits,
			current: minutes === current,
		}));
}

/** Why a figure disagrees with the one printed beside it. Absent where the
 *  surface prints no second figure of the same thing — a section invented to
 *  fill the shape would be prose about nothing. */
function neighbourOf(
	subject: string,
	days: readonly AnalyticsReport.ReportedDay[],
): ProvenanceAnswer["neighbour"] {
	const count = (pick: (row: AnalyticsReport.ReportedDay) => number): string =>
		formatNumber(sum(days.map(pick)), FIGURE_LOCALE);
	if (subject === "visits")
		return {
			claim: {
				key: "admin.analytics.drill.provVisitsClaim",
				params: {
					visits: count((row) => row.visits),
					visitors: count((row) => row.visitors),
				},
			},
			because: { key: "admin.analytics.drill.provVisitsBecause" },
		};
	if (subject === "visitors" || subject === "returning")
		return {
			claim: { key: "admin.analytics.drill.provVisitorsClaim" },
			because: { key: "admin.analytics.drill.provVisitorsBecause" },
		};
	if (subject === "dwell")
		return {
			claim: { key: "admin.analytics.drill.provDwellClaim" },
			because: { key: "admin.analytics.drill.provDwellBecause" },
		};
	if (subject === "funnel.eventPageLands" || subject === "conversion")
		return {
			claim: {
				key: "admin.analytics.drill.provBasketClaim",
				params: {
					baskets: count((row) => row.funnel.eventPageLands),
					visits: count((row) => row.visits),
				},
			},
			because: { key: "admin.analytics.drill.provBasketBecause" },
		};
	if (subject === "revenue")
		return {
			claim: {
				key: "admin.analytics.drill.provRevenueClaim",
				params: { paid: count((row) => row.funnel.paid) },
			},
			because: { key: "admin.analytics.drill.provRevenueBecause" },
		};
	return null;
}

function sum(values: readonly number[]): number {
	return values.reduce((total, value) => total + value, 0);
}

function revenueOf(days: readonly AnalyticsReport.ReportedDay[]): number {
	const byDay = answer().revenueCentsByDay;
	return sum(days.map((row) => byDay[row.date] ?? 0));
}

/** Whether a node's own span is short enough to read as days. A node already
 *  scoped to one week or one day always reads as days — it is the level the
 *  week was opened to reach. */
function readsAsDays(node: DrillNode): boolean {
	return (
		scopeValue(node, "week") !== null ||
		scopeValue(node, "day") !== null ||
		daysOf(node).length <= DAYS_READ_DIRECTLY
	);
}

/** One time part per day, or per week where a day-per-part would be confetti.
 *  `dayOpens` is what a single day leads to; a week always leads back into its
 *  own days under the same subject. */
function timeParts(
	node: DrillNode,
	value: (day: AnalyticsReport.ReportedDay) => number,
	dayOpens: (date: string) => DrillNode | null,
): readonly DrillPart[] {
	const days = daysOf(node);
	if (readsAsDays(node))
		return days.map((row) => ({
			id: row.date,
			label: { day: row.date },
			value: value(row),
			open: dayOpens(row.date),
		}));
	return weeksOf(days).map((week) => ({
		id: week.id,
		label: weekLabel(week.id),
		value: sum(week.days.map(value)),
		open: {
			kind: "parts",
			subject: node.subject,
			scope: [`week:${week.id}`],
		},
	}));
}

/**
 * A decomposition with no rows in it is not an answer — a range with no days
 * yet, a channel the answer no longer names — so the figure above it prints
 * plain rather than offering a gesture that opens an empty panel. A wall is
 * exempt: its whole content is the refusal, and it has no rows to be empty of.
 * One rule at the one exit, so no caller has to remember it.
 */
export function resolveDrill(node: DrillNode): DrillAnswer | null {
	const answered = answerFor(node);
	if (answered === null || !hasContent(answered)) return null;
	// The parts a panel prints are filtered by the SAME rule the figures on the
	// surface are: a part opens something or it prints plain. Filtering here
	// rather than at each site that builds a part is what keeps it one rule —
	// the earlier arrangement let a part offer a records node the records body
	// had nothing to answer with, and the press opened an empty panel.
	return answered.body === "parts"
		? {
				...answered,
				parts: answered.parts.map((part) => ({
					...part,
					open: part.open !== null && answerable(part.open) ? part.open : null,
				})),
			}
		: answered;
}

/** Every open panel's answer, one derivation for the whole chain. A panel is
 *  a `computed` read rather than a resolve inside `render()` because the rows
 *  behind a figure are re-summed out of the console's own live orders and
 *  tickets: with N panels open and a door scanning, resolving in `render()`
 *  rebuilt every one of them on every unrelated re-render. */
export const drillAnswers = computed<readonly (DrillAnswer | null)[]>(() =>
	drillChain.value.map(resolveDrill),
);

/** Whether a panel has anything in it. A decomposition with no rows and a
 *  record list with no records are both absences; a wall's whole content is
 *  its refusal, so it is never empty. */
function hasContent(answered: DrillAnswer): boolean {
	if (answered.body === "parts") return answered.parts.length > 0;
	if (answered.body === "records") return answered.rows.length > 0;
	return true;
}

/**
 * Whether pressing a node opens something. Deliberately `answerFor` and not
 * `resolveDrill`: the filtered form would ask the same question of its own
 * parts, and a wall's doors lead back into parts that lead back into walls, so
 * the check would never terminate. One level is all a press needs.
 */
export function answerable(node: DrillNode): boolean {
	const answered = answerFor(node);
	return answered !== null && hasContent(answered);
}

/** The drill map, one switch. */
function answerFor(node: DrillNode): DrillAnswer | null {
	if (node.kind === "boundary") return wall(node);
	if (node.kind === "records") return records(node);
	if (node.kind === "provenance") return account(node);
	if (node.kind !== "parts") return null;
	const days = daysOf(node);
	const scopedDay = scopeValue(node, "day");

	switch (node.subject) {
		case "visits": {
			// A day, then the campaign that brought it. The campaign rows never
			// cover the whole: a visit that named no campaign carries no source,
			// so the remainder is its own named part rather than a gap.
			if (scopedDay !== null) {
				const day = days[0];
				const named = Object.entries(day?.utm ?? {})
					.map(([source, cell]) => ({
						id: source,
						label: channelLabel(source),
						value: cell.visits,
						open:
							source === BOUNDED_SOURCE
								? {
										kind: "boundary" as const,
										subject: "campaign.bounded",
										scope: node.scope,
									}
								: {
										kind: "parts" as const,
										subject: "channel",
										scope: [...node.scope, `channel:${source}`],
									},
					}))
					.filter((part) => part.value > 0)
					.toSorted((a, b) => b.value - a.value);
				const direct = (day?.visits ?? 0) - sum(named.map((p) => p.value));
				return {
					body: "parts",
					title: {
						key: "admin.analytics.drill.titleVisitsByCampaign",
						params: { day: { day: scopedDay } },
					},
					whole: day?.visits ?? 0,
					unit: "count",
					parts: [
						...named,
						{
							id: "direct",
							label: { key: "admin.analytics.channelDirect" },
							value: Math.max(0, direct),
							open: null,
						},
					],
					claim: { key: "admin.analytics.drill.claimVisitsCampaign" },
				};
			}
			const byDay = readsAsDays(node);
			return {
				body: "parts",
				title: {
					key: byDay
						? "admin.analytics.drill.titleVisitsByDay"
						: "admin.analytics.drill.titleVisitsByWeek",
				},
				whole: sum(days.map((row) => row.visits)),
				unit: "count",
				parts: timeParts(
					node,
					(row) => row.visits,
					(date) => ({
						kind: "parts",
						subject: "visits",
						scope: [`day:${date}`],
					}),
				),
				claim: {
					key: byDay
						? "admin.analytics.drill.claimVisitsDays"
						: "admin.analytics.drill.claimVisitsWeeks",
				},
			};
		}

		case "channel": {
			const source = scopeValue(node, "channel");
			// The direct remainder has no campaign key, so it has no join to the
			// checkout a visit later opened. It is never offered as a channel.
			if (source === null || source === DIRECT_CHANNEL) return null;
			if (source === BOUNDED_SOURCE)
				return wall({ ...node, kind: "boundary", subject: "campaign.bounded" });
			const visits = sum(days.map((row) => row.utm[source]?.visits ?? 0));
			const checkouts = sum(days.map((row) => row.utm[source]?.checkouts ?? 0));
			const paid = sum(days.map((row) => row.utm[source]?.paid ?? 0));
			return {
				body: "parts",
				title:
					scopedDay === null
						? {
								key: "admin.analytics.drill.titleChannel",
								params: { channel: source },
							}
						: {
								key: "admin.analytics.drill.titleChannelOnDay",
								params: { channel: source, day: { day: scopedDay } },
							},
				whole: visits,
				unit: "count",
				parts: [
					{
						id: "paid",
						label: { key: "admin.analytics.drill.partPaid" },
						value: paid,
						// A measurement becomes a business record here — but only
						// where a day is in scope, since the orders list is a day's
						// list and a whole range would be a different page.
						open:
							scopedDay === null
								? null
								: {
										kind: "records",
										subject: "orders",
										scope: [`day:${scopedDay}`, `channel:${source}`],
									},
					},
					{
						id: "unpaid",
						label: { key: "admin.analytics.drill.partUnpaid" },
						value: Math.max(0, checkouts - paid),
						open: { kind: "parts", subject: "drop.payment", scope: node.scope },
					},
					{
						id: "left",
						label: { key: "admin.analytics.drill.partLeft" },
						value: Math.max(0, visits - checkouts),
						open: privacyEdge(node.scope),
					},
				],
				claim: { key: "admin.analytics.drill.claimChannel" },
			};
		}

		case "visitors":
		case "returning":
			return {
				body: "parts",
				title: { key: "admin.analytics.drill.titleBrowsers" },
				whole: sum(days.map((row) => row.visitors)),
				unit: "count",
				parts: [
					{
						id: "new",
						label: { key: "admin.analytics.drill.partFirstSeen" },
						value: sum(days.map((row) => row.newBuyers)),
						open: privacyEdge(node.scope),
					},
					{
						id: "returning",
						label: { key: "admin.analytics.drill.partReturning" },
						value: sum(days.map((row) => row.returningBuyers)),
						open: privacyEdge(node.scope),
					},
				],
				claim: { key: "admin.analytics.drill.claimBrowsers" },
			};

		case "revenue": {
			const byDay = readsAsDays(node);
			return {
				body: "parts",
				title: {
					key: byDay
						? "admin.analytics.drill.titleRevenueByDay"
						: "admin.analytics.drill.titleRevenueByWeek",
				},
				whole: revenueOf(days),
				unit: "money",
				parts: timeParts(
					node,
					(row) => answer().revenueCentsByDay[row.date] ?? 0,
					(date) => ({
						kind: "records",
						subject: "orders",
						scope: [`day:${date}`],
					}),
				),
				claim: { key: "admin.analytics.drill.claimRevenue" },
			};
		}

		case "drop.payment":
			return {
				body: "parts",
				title: { key: "admin.analytics.drill.titleDropPayment" },
				whole: sum(days.map((row) => row.funnel.authorising - row.funnel.paid)),
				unit: "count",
				parts: [
					...(
						["soldOut", "notEligible", "internal", "licenceReadOnly"] as const
					).map((reason) => ({
						id: reason,
						label: { key: OUTCOME_LABEL[reason] },
						value: sum(days.map((row) => row.funnel.refused[reason] ?? 0)),
						open: null,
					})),
					{
						id: "abandoned",
						label: { key: OUTCOME_LABEL.abandoned },
						value: sum(days.map((row) => row.funnel.abandoned)),
						open: privacyEdge(node.scope),
					},
					{
						id: "expired",
						label: { key: OUTCOME_LABEL.expired },
						value: sum(days.map((row) => row.funnel.expired)),
						open: null,
					},
				],
				claim: { key: "admin.analytics.drill.claimDropPayment" },
			};

		case "funnel.eventPageLands":
		case "funnel.contactCompleted":
		case "funnel.authorising":
		case "funnel.paid": {
			const stage = node.subject.slice(
				"funnel.".length,
			) as keyof typeof STAGE_LABEL;
			const byDay = readsAsDays(node);
			return {
				body: "parts",
				title: {
					key: byDay
						? "admin.analytics.drill.titleStageByDay"
						: "admin.analytics.drill.titleStageByWeek",
					params: { stage: { key: STAGE_LABEL[stage] } },
				},
				whole: sum(days.map((row) => row.funnel[stage])),
				unit: "count",
				parts: timeParts(
					node,
					(row) => row.funnel[stage],
					(date) =>
						stage === "paid"
							? { kind: "records", subject: "orders", scope: [`day:${date}`] }
							: null,
				),
				claim: { key: "admin.analytics.drill.claimStage" },
			};
		}

		default:
			return null;
	}
}

/**
 * What one crumb says. A crumb names the STEP, not the figure: the step into a
 * figure's own workings is "Sources" wherever it is taken from, the step that
 * stops is "Limit", and the step out of measurement into the books is
 * "Orders" — so a trail reads as a route rather than as the same noun repeated
 * at two depths. Only a decomposition is named by the slice it was taken
 * under, because there the slice IS the step.
 */
export function crumbLabel(node: DrillNode): Phrase {
	if (node.kind === "provenance")
		return { key: "admin.analytics.drill.crumbSources" };
	if (node.kind === "boundary")
		return { key: "admin.analytics.drill.crumbLimit" };
	if (node.kind === "records")
		return {
			key:
				node.subject === "tickets"
					? "admin.analytics.drill.crumbTickets"
					: "admin.analytics.drill.crumbOrders",
		};
	const last = node.scope.at(-1);
	return last === undefined ? subjectPhrase(node.subject) : scopeLabel(last);
}

// ── The chain as a link ──────────────────────────────────────────────────────
// A drill is a claim about a number, and a claim is worth passing on. The chain
// serialises into one query parameter, legibly rather than compactly, because
// the recipient reads the link before they open it.

/** The field separator inside one node. It is `~` because a subject carries
 *  its own dots (`funnel.paid`, `visitor.timeline`) and a scope carries its own
 *  colons and dashes (`day:2026-08-22`) — a separator any of them could contain
 *  would decode a chain into a node nobody wrote. `~` is unreserved, so the
 *  link stays readable in a message rather than arriving percent-escaped. */
const FIELD = "~";
const NODE = "/";

export function encodeChain(chain: readonly DrillNode[]): string {
	return chain
		.map((node) => [node.kind, node.subject, ...node.scope].join(FIELD))
		.join(NODE);
}

/** The subjects each kind has an answer for — the arms of `answerFor`,
 *  `figureOf`, `wall` and `records`. A pasted link is checked against this
 *  shape rather than against `resolveDrill`, because the answer, the console's
 *  orders and its tickets all land after the surface connects: a real link
 *  resolves to nothing for that first beat, and would be dropped by it. */
const FIGURES = [
	"visits",
	"visitors",
	"returning",
	"revenue",
	"channel",
	"drop.payment",
	"funnel.eventPageLands",
	"funnel.contactCompleted",
	"funnel.authorising",
	"funnel.paid",
] as const;
const RECORDS = ["orders", "tickets"] as const;
const BOUNDARIES = ["visitor.timeline", "campaign.bounded"] as const;
const SUBJECTS: Readonly<Record<DrillKind, ReadonlySet<string>>> = {
	parts: new Set(FIGURES),
	// The attribution line under every other panel opens that panel's own
	// subject as provenance, so provenance answers all of theirs.
	provenance: new Set([
		...FIGURES,
		...RECORDS,
		...BOUNDARIES,
		"conversion",
		"dwell",
		"retrieval",
		"refusal",
	]),
	records: new Set(RECORDS),
	boundary: new Set(BOUNDARIES),
};

/** A link is somebody else's text, so it opens only the prefix of levels that
 *  name a real kind and a subject that kind answers: the first one that does
 *  not ends the chain, because every level after it was a level of a panel
 *  that never opens. A press never comes through here. */
export function decodeChain(raw: string): DrillNode[] {
	const chain: DrillNode[] = [];
	for (const segment of raw.split(NODE)) {
		if (segment.length === 0) continue;
		const [kind = "", subject = "", ...scope] = segment.split(FIELD);
		if (!Object.hasOwn(SUBJECTS, kind)) break;
		if (!SUBJECTS[kind as DrillKind].has(subject)) break;
		chain.push({ kind: kind as DrillKind, subject, scope });
	}
	return chain;
}

/** The URL this chain is reachable at — the page the operator is on, with the
 *  drill parameter replaced. The trail's crumbs carry it, so every level of an
 *  open chain is copyable without a second affordance beside the trail. */
export function chainHref(chain: readonly DrillNode[]): string {
	const params = new URLSearchParams(location.search);
	if (chain.length === 0) params.delete("drill");
	else params.set("drill", encodeChain(chain));
	const query = params.toString();
	return `${location.pathname}${query ? `?${query}` : ""}`;
}
